import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { cn } from "@volca/ui/lib/utils";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { ApprovalMark } from "./approval-mark";
import { SheetApprovalPanel } from "./sheet-approval-panel";
import type { ApprovalChangeHandler, PendingApproval } from "./types";
import { getSheetApprovalSubject } from "./utils";

interface SheetApprovalControlProps {
  accessKey: IPayrollSheetApprovalAccessKey;
  sheetId?: string;
  pendingApproval?: PendingApproval | null;
  /** z-index that clears the viewer's fullscreen overlay. */
  overlayClassName?: string;
  onApprovalChange?: ApprovalChangeHandler;
}

/**
 * Sign-off for the sheet you are on, in the workbook's own toolbar alongside the
 * other controls that act on the active sheet.
 *
 * Deliberately NOT in the sheet tab strip at the bottom: that strip has to keep
 * scrolling 40+ tabs, and its right end is where the floating support bubble
 * sits, so a control parked there gets crowded and half-covered. Up here it also
 * survives fullscreen, which is exactly when someone is reading the numbers they
 * are about to sign off.
 */
export const SheetApprovalControl = ({
  accessKey,
  sheetId,
  pendingApproval,
  overlayClassName,
  onApprovalChange,
}: SheetApprovalControlProps) => {
  const subject = getSheetApprovalSubject(accessKey, sheetId);
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            aria-label={`Approvals for ${subject?.name ?? "current sheet"}`}
            className="group h-auto gap-2 self-stretch rounded-none border-l-line px-3 font-normal text-subtle"
          />
        }
      >
        {subject && <ApprovalMark accessKey={subject} size="md" />}
        <span className="whitespace-nowrap">Approvals</span>
        <CaretDownIcon
          aria-hidden="true"
          className="h-3.5 w-3.5 transition-transform duration-200 group-data-popup-open:rotate-180"
        />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        className={cn("w-88 p-0", overlayClassName)}
      >
        <SheetApprovalPanel
          accessKey={accessKey}
          sheetId={sheetId}
          pendingApproval={pendingApproval}
          onApprovalChange={onApprovalChange}
        />
      </PopoverContent>
    </Popover>
  );
};
